import { Tooltip } from 'antd';
import {
  Attributes,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createEditor } from 'slate';
import { RenderElementProps, Slate, withReact } from 'slate-react';
import styled, { css } from 'styled-components';

import theme from '../../../../../assets/theme';
import { parseMarkdownToSlateV2 } from '../../../../../utils/richTextUtils';
import {
  CuripodDecendant,
  CuripodElement,
  SlateEditable,
} from '../../../../SlideComponents/slateUtil';

const isSlate = (text: string) => {
  return text.startsWith('[{') && text.endsWith('}]');
};

export const MarkdownSlateViewer = ({ content }: { content: string }) => {
  const editor = useMemo(() => withReact(createEditor()), []);
  const [parsedContent, setAsyncParsedContent] = useState<CuripodDecendant[]>([]);

  useEffect(() => {
    const asyncFn = async () => {
      if (isSlate(content)) {
        setAsyncParsedContent(JSON.parse(content) as CuripodDecendant[]);
      } else {
        const parsed = await parseMarkdownToSlateV2(content);
        setAsyncParsedContent(parsed as CuripodDecendant[]);
      }
    };
    asyncFn();
  }, [content]);

  const renderElement = useCallback(
    //@ts-ignore
    (props: RenderElementProps) => <MarkdownElement {...props} />,
    [],
  );
  const renderLeaf = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => {
      return <Leaf {...props} />;
    },
    [],
  );

  useEffect(() => {
    editor.children = parsedContent;
    editor.onChange();
  }, [parsedContent, editor]);

  return (
    <AgentSlateWrapper>
      <span translate="no">
        <Slate editor={editor} value={parsedContent}>
          <SlateEditable
            readOnly={true}
            $readOnly={true}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            style={{
              backgroundColor: 'transparent',
              padding: 0,
              margin: 0,
              border: 'none',
              boxShadow: 'none',
            }}
          />
        </Slate>
      </span>
    </AgentSlateWrapper>
  );
};

// This wrappers adds a bit of spacing between the top level blocks and nested blocks
const AgentSlateWrapper = styled.div`
  position: relative;
  width: 100%;
  ul,
  ol {
    padding-inline-start: 10px;
  }

  ul,
  p,
  ol {
    margin-bottom: 4px; // Default 4px margin on all nested blocks
  }

  div > p {
    margin-bottom: 12px; // Default 12px margin on top level blocks
  }
  div > ul {
    margin-bottom: 12px; // Default 12px margin on top level blocks
  }
  div > ol {
    margin-bottom: 12px; // Default 12px margin on top level blocks
  }
`;

/* Markdown elements have a margin-bottom  */
export const MarkdownElement = ({
  attributes,
  children,
  element,
  className,
}: PropsWithChildren<{
  attributes: Attributes;
  element: CuripodElement;
  className?: string;
}>) => {
  const style = { textAlign: element.align };
  switch (element.type) {
    case 'bulleted-list':
      return (
        <UnorderedListMarkdown
          style={style}
          {...attributes}
          $fontSize={element.size}
          className={className}
          $align={element.align}
          $color={element.color}
        >
          {children}
        </UnorderedListMarkdown>
      );
    case 'numbered-list':
      return (
        <OrderedListMarkdown
          style={style}
          {...attributes}
          $fontSize={element.size}
          className={className}
          $align={element.align}
          $color={element.color}
        >
          {children}
        </OrderedListMarkdown>
      );
    case 'list-item':
      return (
        <ListItem
          style={style}
          $color={element.color}
          {...attributes}
          $fontSize={element.size}
          $align={element.align}
          className={className}
        >
          {children}
        </ListItem>
      );
    default:
      return (
        <ParagraphElementMarkdown
          {...attributes}
          style={style}
          $color={element.color}
          $fontSize={element.size}
          $align={element.align}
          className={className}
        >
          {children}
        </ParagraphElementMarkdown>
      );
  }
};

export const Leaf = ({
  attributes,
  children,
  leaf,
}: {
  attributes: Attributes;
  children: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  leaf: any;
}) => {
  if (leaf.bold) {
    children = (
      <SlateLeafSpan $color={leaf.color}>
        <strong>{children}</strong>
      </SlateLeafSpan>
    );
  }
  if (leaf.underline) {
    children = (
      <SlateLeafSpan $color={leaf.color}>
        <u>{children}</u>
      </SlateLeafSpan>
    );
  }
  if (leaf.italic) {
    children = (
      <SlateLeafSpan $color={leaf.color}>
        <em style={{ color: leaf.color }}>{children}</em>
      </SlateLeafSpan>
    );
  }
  if (leaf.link) {
    children = (
      <Tooltip mouseEnterDelay={0.5} title={leaf.link}>
        <SlateLeafSpan
          $color={leaf.color !== theme.colors.black ? leaf.color : theme.colors.blueDark}
          style={{
            textDecoration: 'underline',
            filter: 'brightness(0.8)',
          }}
        >
          <a href={leaf.link} target="_blank" rel="noreferrer">
            {children}
          </a>
        </SlateLeafSpan>
      </Tooltip>
    );
  }
  return (
    <SlateLeafSpan $color={leaf.color} {...attributes}>
      {children}
    </SlateLeafSpan>
  );
};

const SlateLeafSpan = styled.span<{ $color?: string }>`
  color: ${({ $color }) =>
    $color}; // we dont set default black to allow for inheritance and backward compatibility with paragraph element color
  span::selection {
    color: ${({ $color }) => ($color ? $color : 'white')} !important;
    background-color: ${theme.colors.orange} !important;
  }
  a {
    color: ${({ $color }) => ($color ? $color : theme.colors.blueDark)};
  }
`;

export const ParagraphElementMarkdown = styled.p<{
  $fontSize?: number;
  $color?: string;
  $align?: string;
}>`
  height: min-content;
  color: ${({ $color }) => ($color ? $color : 'black')};
  white-space: pre-line;
  text-align: ${({ $align }) => ($align ? $align : 'center')};
  ${({ $fontSize }) =>
    $fontSize &&
    css`
      font-size: ${$fontSize}px;
    `}
`;

const UnorderedListMarkdown = styled.ul<{
  $fontSize?: number;
  $color?: string;
  $align?: string;
}>`
  color: ${({ $color }) => ($color ? $color : 'black')};
  ${({ $fontSize }) =>
    $fontSize &&
    css`
      font-size: ${$fontSize}px;
      margin-left: ${$fontSize}px;
    `}

  &::marker {
    ${({ $fontSize }) =>
      $fontSize &&
      css`
        font-size: ${$fontSize}px;
      `}
    color: ${({ $color }) => ($color ? $color : 'black')};
  }
  text-align: ${({ $align }) => ($align ? $align : 'center')};

`;

const OrderedListMarkdown = styled.ol<{
  $fontSize?: number;
  $color?: string;
  $align?: string;
}>`
  color: ${({ $color }) => ($color ? $color : 'black')};
  ${({ $fontSize }) =>
    $fontSize &&
    css`
      font-size: ${$fontSize}px;
      margin-left: ${$fontSize}px;
    `}

  &::marker {
    ${({ $fontSize }) =>
      $fontSize &&
      css`
        font-size: ${$fontSize}px;
      `}
    color: ${({ $color }) => ($color ? $color : 'black')};
  }
  text-align: ${({ $align }) => ($align ? $align : 'center')};
`;

const ListItem = styled.li<{ $fontSize?: number; $color?: string; $align?: string }>`
  color: ${({ $color }) => ($color ? $color : 'black')};
  text-align: ${({ $align }) => ($align ? $align : 'center')};
`;
